"use client";
import React, { HTMLAttributes } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef as TanstackColumnDef,
  getSortedRowModel,
  ColumnSort,
  OnChangeFn,
  SortingState,
} from "@tanstack/react-table";
import cn from "classnames";
import Icon from "./icon";
import NotFound from "./not-found";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import "./scroll-hint.css";

export interface AdditionalColumnDefs {
  headerClassNames?: string;
  cellClassNames?: string;
}

export type ColumnDef<T> = TanstackColumnDef<T> & {
  fallback?: React.ReactNode;
  minWidth?: string;
  width?: string;
  maxWidth?: string;
  prependOrder?: boolean;
};

interface TableProps<T> extends HTMLAttributes<HTMLTableElement> {
  type: "standard" | "banded" | "separator";
  header?: { title: React.ReactNode };
  columns: ColumnDef<T>[];
  data?: T[];
  loading?: boolean;
  initialSort?: {column: string, direction: "asc" | "desc"};
  onSortingChange?: (sorting: ColumnSort[]) => void;
  headerClasses?: string;
  cellClasses?: string;
  cellVAlign?: "top" | "middle";
  fallback?: React.ReactNode;
  fallbackRows?: number;
  error?: boolean;
  footer?: React.ReactNode;
  getRowHref?: (row: T) => string | undefined;
  scrollHint?: {
    show?: boolean;
    left?: number;
    right?: number;
  };
}

export default function Table<T>(props: TableProps<T>) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const hintRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const scrollElement = scrollRef.current;
    const hintElement = hintRef.current;

    if (scrollElement && hintElement) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollElement;
      const maxScrollLeft = scrollWidth - clientWidth;
      const padding = 30;
      const showLeftHint =
        (scrollLeft > 0 && scrollLeft >= maxScrollLeft - padding) ||
        (props.scrollHint?.show &&
          scrollLeft > 0 &&
          scrollLeft < maxScrollLeft - padding);
      const showRightHint = scrollLeft < maxScrollLeft - padding;
      if (showRightHint) {
        hintElement.classList.add("scroll-visible-right");
      } else {
        hintElement.classList.remove("scroll-visible-right");
      }
      if (showLeftHint) {
        hintElement.classList.add("scroll-visible-left");
      } else {
        hintElement.classList.remove("scroll-visible-left");
      }
    }
  };

  React.useEffect(() => {
    const element = scrollRef.current;
    if (element) {
      element.scrollLeft = 0;
      element.addEventListener("scroll", handleScroll);
      handleScroll();

      return () => element.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const [sorting, setSorting] = React.useState<ColumnSort[]>(() => {
    if (props.initialSort) {
      return [
        {
          id: props.initialSort.column,
          desc: props.initialSort.direction === "desc",
        },
      ];
    }
    return [];
  });

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSorting = typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;
    setSorting(newSorting);
    props.onSortingChange?.(newSorting);
};
  const table = useReactTable<T>({
    data: props.data || [],
    columns: props.columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    state: {
      sorting: sorting,
    },
    onSortingChange: handleSortingChange,
  });
  const hasData = props.data && props.data.length > 0;
  const fallbackRows = props.fallbackRows || 8;
  return (
    <>
      {props.header && (
        <div className="text-label-lg-bold uppercase text-secondary mb-4">
          {props.header.title}
        </div>
      )}
      <ScrollArea.Root>
        <div className="scroll-hint" ref={hintRef}>
          <div
            className="gradient-left"
            style={{
              left:
                props.scrollHint && props.scrollHint.left
                  ? `${props.scrollHint.left}px`
                  : undefined,
            }}
          ></div>
          <div
            className="gradient-right"
            style={{
              left:
                props.scrollHint && props.scrollHint.right
                  ? `${props.scrollHint.right}px`
                  : undefined,
            }}
          ></div>
          <ScrollArea.Viewport ref={scrollRef}>
            <table className={cn(`${props.type}`, props.className)}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{
                          minWidth: (header.column.columnDef as ColumnDef<T>)
                            ?.minWidth,
                          width: (header.column.columnDef as ColumnDef<T>)
                            ?.width,
                          maxWidth: (header.column.columnDef as ColumnDef<T>)
                            ?.maxWidth,
                        }}
                        className={`${
                          (header.column.columnDef.meta as any)?.classNames ??
                          ""
                        } ${header.column.getCanSort() && "cursor-pointer"} ${
                          header.column.getCanSort() &&
                          header.column.getIsSorted() &&
                          "highlighted"
                        }`}
                      >
                        <div
                          className={cn(
                            `flex flex-row gap-0.5 items-center no-link`,
                            props.headerClasses
                          )}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <div className="flex justify-center items-center w-4 h-4">
                              {!header.column.getIsSorted() ? (
                                <Icon
                                  name="table-toggle"
                                  size="sm"
                                  className="fill-secondary"
                                />
                              ) : header.column.getIsSorted() === "asc" ? (
                                <Icon
                                  name="arrow-button-up"
                                  className="w-3 h-3 fill-secondary"
                                />
                              ) : header.column.getIsSorted() === "desc" ? (
                                <Icon
                                  name="arrow-button-down"
                                  className="w-3 h-3 fill-secondary"
                                />
                              ) : null}
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {props.loading ? (
                  Array.from({ length: fallbackRows }).map((_, index) => (
                    <tr key={index}>
                      {props.columns.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className={`animate-pulse h-12 ${props.cellClasses}`}
                        ></td>
                      ))}
                    </tr>
                  ))
                ) : hasData ? (
                  <>
                    {table.getRowModel().rows.map((row, rowIndex) => {
                      const rowHref = props.getRowHref
                        ? props.getRowHref(row.original)
                        : undefined;
                      return (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className={`no-link ${
                                (cell.column.columnDef.meta as any)
                                  ?.classNames ?? ""
                              } ${cell.column.getIsSorted() && "highlighted"} ${
                                props.cellVAlign === "top"
                                  ? "align-top"
                                  : "align-middle"
                              }`}
                            >
                              <div
                                className={cn(
                                  `flex flex-row whitespace-nowrap gap-4 items-center`,
                                  props.cellClasses
                                )}
                              >
                                {(cell.column.columnDef as ColumnDef<T>)
                                  ?.prependOrder && (
                                  <span className="min-w-[16px]">
                                    {rowIndex + 1}
                                  </span>
                                )}
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </div>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </>
                ) : props.fallback ? (
                  <tr>
                    <td colSpan={props.columns.length}>{props.fallback}</td>
                  </tr>
                ) : !props.fallback ? (
                  <tr>
                    <td colSpan={props.columns.length}>
                      <NotFound label="No data found" icon="target-miss" />
                    </td>
                  </tr>
                ) : fallbackRows > 0 ? (
                  Array.from({ length: fallbackRows }).map((_, index) => (
                    <tr key={index}>
                      {props.columns.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className={`animate-pulse h-12 ${props.cellClasses}`}
                        ></td>
                      ))}
                    </tr>
                  ))
                ) : undefined}
              </tbody>
            </table>
          </ScrollArea.Viewport>
        </div>
        <ScrollArea.Scrollbar
          orientation="horizontal"
          className="flex select-none touch-none bg-transparent transition-colors duration-[160ms] ease-out hover:bg-blackA5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-1"
        >
          <ScrollArea.Thumb className="flex-1 bg-secondary-500 rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[44px] before:min-h-[44px]" />
        </ScrollArea.Scrollbar>
        <ScrollArea.Corner />
      </ScrollArea.Root>
      {props.footer}
    </>
  );
}
